import {
  ItemListItemRowData,
  ItemListRowType,
} from "@/components/item-list/types/item-list-row-types";
import { ItemListItemKind } from "@/components/item-list/types/item-list-item-types";
import { generateShortDateString } from "@/domains/date/date";
import { MDSIconKind, MDSMenuContentList } from "@/mds";
import { AppStore } from "@/store/AppStore";
import { ListStateObservable } from "@/store/pages/ListStateObservable";
import { SpaceAccountCalendarEventObservable } from "@/store/calendar/space-account-calendar-event/SpaceAccountCalendarEventObservable";
import { DateTime } from "luxon";
import { itemsModule } from "@/modules/items";
import { uuidModule } from "@/modules/uuid";
import { UNTITLED_CALENDAR_EVENT_TITLE } from "@/domains/untitled/untitled";
import { resolveSourceIdForCalendarEvent } from "@/modules/uuid/sync-models/resolveSourceIdForCalendarEvent";
import { sortBy } from "lodash-es";
import { trackEvent } from "@/domains/metrics/segment";
import { TrackedEvent } from "@/domains/metrics/tracked-event";
import { EventContext } from "@/domains/metrics/context";

export const getItemListRowForCalendarEvent = ({
  listState,
  calendarEventObservable,
  store,
  dropdown,
}: {
  listState?: ListStateObservable;
  calendarEventObservable: SpaceAccountCalendarEventObservable;
  store: AppStore;
  dropdown?: MDSMenuContentList;
}) => {
  const eventId = calendarEventObservable.id;
  const itemAppearance = itemsModule.resolveAppearance({
    itemId: eventId,
    appStore: store,
  });

  const startTime = calendarEventObservable.startTime
    ? DateTime.fromISO(calendarEventObservable.startTime)
    : null;
  const dateLabel = startTime ? generateShortDateString(startTime) : undefined;
  const associatedNote = calendarEventObservable.associatedNote;

  const row: ItemListItemRowData = {
    type: ItemListRowType.Item,
    key: eventId,
    payload: {
      id: eventId,
      kind: ItemListItemKind.CalendarEvent,
      titleIcon: { kind: MDSIconKind.Calendar },
      appearance: itemAppearance,
      label: calendarEventObservable.primaryLabel || UNTITLED_CALENDAR_EVENT_TITLE,
      onClick: async () => {
        if (associatedNote) {
          store.navigation.goToNote({ noteId: associatedNote.id });
          return;
        }
        // If associated note wasn't loaded yet.
        const sourceId = resolveSourceIdForCalendarEvent({
          connectedCalendarEventId: calendarEventObservable.associatedConnectedCalendarEventId,
        });
        const noteSources = await store.noteSources.localTable
          .where("source_id")
          .equals(sourceId)
          .toArray();
        const ids =
          noteSources
            ?.map(noteSource => noteSource.model_data?.note_id)
            .filter((id): id is string => Boolean(id)) ?? [];
        const sortedIds = sortBy(ids);
        const notes = await store.notes.localTable.bulkGet(sortedIds);
        const note = notes.find(note => note?.is_available);
        let noteId = note?.model_id;
        if (!noteId) {
          noteId = uuidModule.generate();
          await store.notes.createNoteUsingCalendarEventId({
            noteId,
            calendarEventId: eventId,
          });
          trackEvent(TrackedEvent.NoteCreate, {
            context: EventContext.CalendarNotesListRow,
            connected_calendar_event_id: calendarEventObservable.associatedConnectedCalendarEventId,
          });
        }
        store.navigation.goToNote({ noteId });
      },
      dateLabel,
      listState,
      dropdown,
    },
  };

  return row;
};
